'use client';

type DataLayer = object;

type DataLayerArgs = {
  /**
   * Object that contains all of the information that you want to pass to Google Tag Manager.
   */
  dataLayer?: DataLayer;
  /**
   * Custom name for dataLayer object.
   */
  dataLayerName?: string;
};

export type TagManagerArgs = DataLayerArgs & {
  /**
   * GTM id, must be something like GTM-000000.
   */
  gtmId: string;
  /**
   * Additional events such as 'gtm.start': new Date().getTime(),event:'gtm.js'.
   */
  events?: object;
  /**
   * Used to set environments.
   */
  auth?: string;
  /**
   * Used to set environments, something like env-00.
   */
  preview?: string;
};

const Snippets = {
  tags: function ({
    gtmId,
    events,
    dataLayer = {},
    dataLayerName = 'dataLayer',
    preview,
    auth,
  }: TagManagerArgs) {
    const gtm_auth = `&gtm_auth=${auth}`;
    const gtm_preview = `&gtm_preview=${preview}`;

    const iframe = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}${gtm_auth}${gtm_preview}&gtm_cookies_win=x"
        height="0" width="0" style="display:none;visibility:hidden" id="tag-manager"></iframe>`;

    const script = `
      (function(w,d,s,l,i){w[l]=w[l]||[];
        w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js', ${JSON.stringify(events).slice(
          1,
          -1
        )}});
        var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
        j.setAttributeNode(d.createAttribute('data-ot-ignore'));j.setAttribute('class','optanon-category-C0001');
        j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl+'${gtm_auth}${gtm_preview}&gtm_cookies_win=x';
        f.parentNode.insertBefore(j,f);
      })(window,document,'script','${dataLayerName}','${gtmId}');`;

    const dataLayerVar = this.dataLayer(dataLayer, dataLayerName);

    return {
      iframe,
      script,
      dataLayerVar,
    };
  },
  dataLayer: function (dataLayer: DataLayer, dataLayerName: string) {
    return `
      window.${dataLayerName} = window.${dataLayerName} || [];
      window.${dataLayerName}.push(${JSON.stringify(dataLayer)})`;
  },
};

export const TagManager = {
  dataScript: function (dataLayer: string): HTMLScriptElement | undefined {
    if (typeof window === 'undefined') return;

    const script = document.createElement('script');
    script.innerHTML = dataLayer;
    return script;
  },
  gtm: function (args: TagManagerArgs) {
    if (typeof window === 'undefined') return;

    const snippets = Snippets.tags(args);

    const noScript = (): HTMLElement => {
      const noscript = document.createElement('noscript');
      noscript.innerHTML = snippets.iframe;
      return noscript;
    };

    const script = (): HTMLElement => {
      const script = document.createElement('script');
      script.innerHTML = snippets.script;
      return script;
    };

    const dataScript = this.dataScript(snippets.dataLayerVar);

    return {
      noScript,
      script,
      dataScript,
    };
  },
  initialize: function ({
    gtmId,
    events = {},
    dataLayer,
    dataLayerName = 'dataLayer',
    auth = '',
    preview = '',
  }: TagManagerArgs) {
    const gtm = this.gtm({
      gtmId,
      events,
      dataLayer,
      dataLayerName,
      auth,
      preview,
    });
    if (!gtm || !gtm.dataScript) return;

    if (dataLayer) document.head.appendChild(gtm.dataScript);
    document.head.insertBefore(gtm.script(), document.head.childNodes[0]);
    document.body.insertBefore(gtm.noScript(), document.body.childNodes[0]);
  },
  dataLayer: function ({ dataLayer = {}, dataLayerName = 'dataLayer' }: DataLayerArgs) {
    if (typeof window === 'undefined') return;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (window[dataLayerName || '']) return window[dataLayerName].push(dataLayer);

    const snippets = Snippets.dataLayer(dataLayer, dataLayerName);
    const dataScript = this.dataScript(snippets);

    if (!dataScript) return;
    document.head.insertBefore(dataScript, document.head.childNodes[0]);
  },
};
